const IMAGE_EXTENSIONS = /\.(apng|avif|bmp|gif|ico|jpe?g|png|svg|webp)$/i;

/**
 * Whether a URL points to an image, judged from the URL alone: one hosted on
 * the knowledge CDN, a path ending in an image file extension, or an image
 * data URL.
 *
 * Imported and pasted articles sometimes load images through an `<iframe>`,
 * which should become an image rather than an embed. Extensionless images on
 * other hosts are not recognised, since telling them apart would mean fetching
 * them.
 */
export function isImageUrl(url: string | null): boolean {
  if (!url) return false;
  if (/^data:image\//i.test(url.trim())) return true;

  const parsed = parseUrl(url);
  if (!parsed) return false;
  if (!['http:', 'https:'].includes(parsed.protocol)) return false;

  return isKnowledgeCdnImage(parsed) || IMAGE_EXTENSIONS.test(parsed.pathname);
}

/**
 * An image on the knowledge CDN, such as
 * `https://knowledge-cdn.euw1.prod.dixa.io/images/…/image/….png`. Matched by
 * location rather than extension, so it holds for extensionless ones too.
 */
function isKnowledgeCdnImage(url: URL): boolean {
  return (
    url.hostname.startsWith('knowledge-cdn.') &&
    url.hostname.endsWith('.dixa.io') &&
    url.pathname.startsWith('/images/')
  );
}

function parseUrl(url: string): URL | undefined {
  try {
    // Base for protocol-relative sources (`//knowledge-cdn…`).
    return new URL(url, 'https://base.invalid');
  } catch {
    return undefined;
  }
}
